import moment from 'moment';
import createCachedSelector, { LruCacheObject } from 're-reselect';
import { Map as ImmutableMap, List as ImmutableList } from 'immutable';

import { getLoadPending as getAccountsLoadPending, getReportableAccountsById, getAccountsById, getSavingAccounts } from 'data/accounts/selectors';
import { getCategoriesById, getLoadPending as getCategoriesLoadPending } from 'data/categories/selectors';
import { getLoadPending as getTransactionsLoadPending, getTransactionsByAccountId } from 'data/transactions/selectors';
import { getAllBalancesByAccountId, getBalancesByAccountId } from 'data/accountBalances/selectors';
import { getTransactionListForAccountIds } from 'data/transactionList/selectors';

import { calculateValuesForPeriods, incomeFilters, monthlyFrequencyData, spendingFilters, createPastMonthPeriods } from 'data/otReports/utils';

import { getLogger } from 'utils/logger';

import { DateTime } from 'luxon';

const log = getLogger('data/otReports/selectors');

/**
 * Get net worth data grouped by periods
 */
export const getNetWorthForPeriods = createCachedSelector(
  (state) => getAccountsLoadPending(state) || getCategoriesLoadPending(state) || getTransactionsLoadPending(state),
  getAllBalancesByAccountId,
  getTransactionListForAccountIds,
  (_, props) => props.numMonths,
  (_, props) => props.dateInterval,
  (isLoadPending, allBalancesByAccountId, transactionListForAccountIds, numMonths, dateInterval) => {

    // accounts, categories, and transactions must all be loaded before we attempt to calculate spending
    if (isLoadPending) {
      return null;
    }
    const txnList = {};
    for (let i=0; i<numMonths; i++) {
      const key = DateTime.local().startOf('month').minus({months: i}).toFormat('yyyy-MM:MMMM yyyy');
      txnList[key] = null;
    }
    Object.entries(transactionListForAccountIds.toJS()).forEach(([key, val]) => {
      if (key.split(':')[0].split('-')[1] !== '00') {
        const theKey = DateTime.fromFormat(key.split(':')[0] || key, 'yyyy-MM');
        if (dateInterval.contains(theKey)) {
          if (Object.prototype.hasOwnProperty.call(txnList, key)) {
            txnList[key] = val;
          }
        }
      }
    });
    /**
     * Get all balances and calculate total net worth
     */
    let totalBalance = 0;
    allBalancesByAccountId.forEach((account) => {
      if(account.accountType !== 'GOAL'){
        totalBalance += account.currentBalance || 0;
      }
    });

    /**
     * Calculate the transactions total per month - ignoring the txns where isExcludedFromReports = true
     */
    const totalsPerMonth = {};

    Object.entries(txnList).forEach(([month, txns]) => {
      if (txnList[month]) {
        let total = 0;
        txns.forEach((txn) => {
          if(!txn.isExcludedFromReports){
            total += txn.amount
          }
        });
        totalsPerMonth[month] = {};
        totalsPerMonth[month].txns = txns;
        totalsPerMonth[month].txnsTotal = total;
      } else {
        totalsPerMonth[month] = {};
        totalsPerMonth[month].txns = [];
        totalsPerMonth[month].txnsTotal = 0;
      }
    });

    /**
     * Calculate net worth per month -
     * We go backwards from the current month where the networth = totalBalance
     * Each month's net worth will be next month's net worth minus the next month's txns total
     */
    const totalsPerMonthKeys = Object.keys(totalsPerMonth);
    let nextMonthKey = null;
    let nextMonthNetWorth = null;
    nextMonthKey = null;

    /**
     * Calculate Net Worth per month and periods data
     */
    totalsPerMonthKeys.forEach((month) => {
      if(nextMonthNetWorth === null){
        totalsPerMonth[month].netWorth = totalBalance;
        nextMonthNetWorth = totalBalance;
      } else {
        totalsPerMonth[month].netWorth = nextMonthNetWorth - totalsPerMonth[nextMonthKey].txnsTotal;
        nextMonthNetWorth = totalsPerMonth[month].netWorth;
      }

      nextMonthKey = month;
      const period = DateTime.fromISO(month.split(':')[0]);
      totalsPerMonth[month].period = period;
      totalsPerMonth[month].label = monthlyFrequencyData.labelFromPeriod(period);
      totalsPerMonth[month].shortLabel = monthlyFrequencyData.shortLabelFromPeriod(period);
      totalsPerMonth[month].year = monthlyFrequencyData.yearFromPeriod(period);
    });

    return {
      periods: ImmutableList(Object.values(totalsPerMonth).reverse()),
      totalNetWorth: totalBalance,
      currency: 'USD',
    };
  }
)(
  // this line sets the key to use for the cache
  (state, props) => props.accountIds ? `${props.accountIds.toString()}${props.startDate}${props.endDate}` : 'any',
  {
    cacheObject: new LruCacheObject({ cacheSize: 6 }),
  }
);

/**
 * Get income data grouped by frequency
 */
export const getIncomeForPeriods = createCachedSelector(
  (state) => getAccountsLoadPending(state) || getCategoriesLoadPending(state) || getTransactionsLoadPending(state),
  getReportableAccountsById,
  (state, props) => props.accountIds,
  getTransactionsByAccountId,
  getCategoriesById,
  (state, props) => props.dateInterval,
  (_, props) => props.frequency,
  (_, props) => props.filterCriteria,
  (isLoadPending, allAccountsByIds, accountIds, transactionsByAccountId, categoriesById, dateInterval, frequency, filterCriteria) => {

    // accounts, categories, and transactions must all be loaded before we attempt to caclculate spending
    if (isLoadPending) {
      return null;
    }

    // turn into trace statement after fixing issues with input properties changing all the time
    log.log('calculating income for', allAccountsByIds, accountIds, dateInterval);

    // will default to a monthly frequency
    let frequencyData = monthlyFrequencyData;
    switch (frequency) {
      case 'monthly':
        frequencyData = monthlyFrequencyData;
        break;
      default:
        break;
    }

    const accountIdsToUse = accountIds || allAccountsByIds.keySeq();
    return calculateValuesForPeriods(accountIdsToUse, allAccountsByIds, categoriesById, transactionsByAccountId, dateInterval, frequencyData, incomeFilters, filterCriteria);
  }
)(
  // this line sets the key to use for the cache
  (state, props) => props.accountIds ? `${props.accountIds.toString()}${props.startDate}${props.endDate}` : 'any',
  {
    cacheObject: new LruCacheObject({ cacheSize: 6 }),
  }
);


/**
 * Get spending data grouped by frequency
 */
export const getSpendingForPeriods = createCachedSelector(
  (state) => getAccountsLoadPending(state) || getCategoriesLoadPending(state) || getTransactionsLoadPending(state),
  getReportableAccountsById,
  (_, props) => props.accountIds,
  getTransactionsByAccountId,
  getCategoriesById,
  (_, props) => props.dateInterval,
  (_, props) => props.frequency,
  (_, props) => props.filterCriteria,
  (isLoadPending, allAccountsByIds, accountIds, transactionsByAccountId, categoriesById, dateInterval, frequency, filterCriteria) => {

    // accounts, categories, and transactions must all be loaded before we attempt to calculate spending
    if (isLoadPending) {
      return null;
    }

    // turn into trace statement after fixing issues with input properties changing all the time
    log.log('calculating spending for', allAccountsByIds, accountIds, dateInterval, frequency);

    const accountIdsToUse = accountIds || allAccountsByIds.keySeq();
    return calculateValuesForPeriods(accountIdsToUse, allAccountsByIds, categoriesById, transactionsByAccountId, dateInterval, monthlyFrequencyData, spendingFilters, filterCriteria);
  }
)(
  // this line sets the key to use for the cache
  (state, { accountIds, dateInterval, frequency }) => {
    const accountIdsKey = accountIds ? `${accountIds.toString()}` : 'all';
    return `${accountIdsKey}_${dateInterval ? dateInterval.toString() : 'default'}_${frequency}`;
  },
  {
    cacheObject: new LruCacheObject({ cacheSize: 10 }),
  }
);


/**
 * Get the combined balance of all savings accounts over past X months
 * */
export const getMonthlySavingsBalances = createCachedSelector(
  (state) => getAccountsLoadPending(state) || getCategoriesLoadPending(state) || getTransactionsLoadPending(state),
  getSavingAccounts,
  getTransactionsByAccountId,
  getAccountsById,
  getBalancesByAccountId,
  (_, props) => props.numMonths,
  (isLoadPending, accounts, txnsByAccountId, accountsById, balancesByAccountId, numMonths) => {
    if (isLoadPending) return null;

    const balanceObjects = accounts.keySeq().map((accountId) => balancesByAccountId.get(accountId));
    // check to make sure we have balance objects or just return balances of 0
    if (balanceObjects.size === 0) return ImmutableMap({ periods: createPastMonthPeriods({ balance: 0 }, numMonths), allTxns: ImmutableList() });

    let currentTotalBalance = 0;
    balanceObjects.forEach((bal) => {currentTotalBalance += bal.currentBalance});

    // leave 'txns' as basic arrays so we can build them easily and convert them to an ImmutableList later, same with 'allTxns'
    const periods = createPastMonthPeriods({ balance: currentTotalBalance }, numMonths);
    const allTxns = [];

    const rangeStart = moment(periods.get(0).date).startOf('month');
    const rangeEnd = moment(periods.get(periods.size-1).date).endOf('month');

    accounts.keySeq().forEach((accountId) => {
      if (txnsByAccountId.get(accountId)) {
        txnsByAccountId.get(accountId).valueSeq().forEach((txn) => {
          const txnMoment = moment(txn.postedOn);
          // filter txns that aren't in the range
          if (txnMoment.isBefore(rangeStart) || txnMoment.isAfter(rangeEnd)) return;

          // add it to allTxns
          allTxns.push(txn);

          // now go through the periods and adjust balances and add txns if it's in the month
          periods.forEach((period) => {
            if (txnMoment.isSameOrAfter(moment(period.date)) && txnMoment.isSameOrBefore(moment(), 'day')) {
              // eslint-disable-next-line no-param-reassign
              period.balance -= txn.amount;
            } else if (txn.isExcludedFromReports) { // if txn occcurred before but we're excluding it
              // eslint-disable-next-line no-param-reassign
              period.balance -= txn.amount  // uncomment to account for ignoring txn
            }
            if (txnMoment.isSameOrAfter(moment(period.date).startOf('month')) && txnMoment.isSameOrBefore(moment(period.date).endOf('month'))) {
              period.mutableTxns.push(txn);
            }
          });
        })
      }
    });

    // convert all the txn lists to ImmutableLists
    // eslint-disable-next-line no-param-reassign
    periods.forEach((period) => {period.txns = ImmutableList(period.mutableTxns)});

    // round off all the balances
    // eslint-disable-next-line no-param-reassign
    periods.forEach((period) => {period.balance = period.balance.toFixed(2)});

    return ImmutableMap({ periods, allTxns: ImmutableList(allTxns) });
  }
)(
  // this line sets the key to use for the cache
  (state, { dateRange }) => `$six-month-savings-${dateRange.toString()}`,
  { cacheObject: new LruCacheObject({ cacheSize: 5 }) }
);
